/**
 * EmpireAggregate — "tu imperio" cross-char tile para el Dashboard.
 *
 * Muestra totales agregados (wallet + SP + active jobs + PI) + breakdown
 * por char del dashboard_user. Single source de verdad para un multiboxer
 * que quiere ver "cómo viene EN TOTAL" sin switchear char por char.
 *
 * Patrón UX:
 *   - Hero numbers arriba (mismo style que el Net worth tile del active char).
 *     4 stats: wallet total, SP, jobs activos, PI (colonies + pins).
 *   - Lista de chars abajo con portrait + name + 4 mini-stats (wallet / SP /
 *     jobs / PI). Active char destacado con checkmark + border accent.
 *   - Si una row individual falló (ESI 5xx), se muestra "data unavailable"
 *     en vez de nulls feos.
 *   - 1 char → muestra igual (overhead mínimo). 0 chars no aplica (debe
 *     haber ≥1 activo para llegar al dashboard).
 *
 * Data flow:
 *   - useQuery con queryKey ['dashboard-aggregate']
 *   - staleTime 5min (matches el KV cache TTL del backend)
 *   - refetchOnWindowFocus: true (refresh al volver al tab)
 *   - on error → banner discreto (el dashboard NO debe romperse)
 */

import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { StatTile } from './ui'

export function EmpireAggregate() {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-aggregate'],
    queryFn: () => api.dashboardAggregate(),
    // 5min = al server KV TTL. Refresh on focus = "volví al tab, refrescame".
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  })

  if (isError) {
    return (
      <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-3 text-eve-danger text-sm">
        Tu imperio no se pudo cargar: {(error as Error).message}. El resto del
        dashboard sigue funcionando.
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-5">
        <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-8 w-2/3 bg-eve-border/30 rounded animate-pulse mt-2" />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="h-16 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-16 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-16 bg-eve-border/30 rounded animate-pulse" />
        </div>
      </div>
    )
  }

  const totals = data.totals
  // chars incluye todos los linkeados (cap 6 del backend). Ordenados por
  // last_login_at DESC desde el backend.
  return (
    <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-border rounded-md p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Tu imperio
          </div>
          <div className="font-display text-xl font-semibold text-eve-text mt-0.5">
            {totals.chars_count}{' '}
            {totals.chars_count === 1 ? 'personaje' : 'personajes'} · agregado
          </div>
        </div>
        <div className="text-[10px] text-eve-muted-2 font-mono italic">
          {data.cached ? 'cached · ' : ''}
          ref {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'} UTC
        </div>
      </div>

      {/* Hero totals — 4 columns (responsive: 1 col → 2 col → 4 col). Cada
          uno con label + big number + hint. PI cierra la métrica cross-char:
          colonies + pins sumados, sin ISK/h (eso requiere detail endpoint
          por colony — fuera de budget para el aggregate). */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          variant="muted"
          size="md"
          tone="accent"
          label="Wallet total"
          value={formatISKShort(totals.wallet)}
          sub={`${formatISK(totals.wallet, 0)} ISK entre todos los chars`}
        />
        <StatTile
          variant="muted"
          size="md"
          tone="accent"
          label="Skill points"
          value={formatISKShort(totals.total_sp)}
          sub={`${totals.total_sp.toLocaleString('en-US')} SP acumulados`}
        />
        <StatTile
          variant="muted"
          size="md"
          tone="accent"
          label="Jobs activos"
          value={String(totals.active_jobs_count)}
          sub="manufactura + reacciones en curso"
        />
        <StatTile
          variant="muted"
          size="md"
          tone="accent"
          label="PI colonies"
          value={String(totals.pi_colonies_count)}
          sub={
            totals.pi_colonies_count > 0
              ? `${totals.pi_total_pins} pins instalados en total`
              : 'sin planetary'
          }
        />
      </div>

      {/* Per-char breakdown */}
      <div className="mt-4 border-t border-eve-border/40 pt-3">
        <ul className="space-y-2">
          {data.chars.map((c) => (
            <CharRow key={c.id} char={c} />
          ))}
        </ul>
      </div>

      {data.note && (
        <div className="mt-3 text-xs text-eve-warn italic border-t border-eve-border/40 pt-2">
          {data.note}
        </div>
      )}
    </div>
  )
}

interface CharRowProps {
  char: {
    id: number
    name: string
    portrait: string | null
    is_active: boolean
    wallet: number | null
    total_sp: number | null
    active_jobs_count: number
    pi_colonies_count: number
    pi_total_pins: number
    error?: string
  }
}

function CharRow({ char }: CharRowProps) {
  const hasData = !char.error
  return (
    <li>
      <div
        className={`flex items-center gap-3 px-3 py-2 rounded border transition-colors ${
          char.is_active
            ? 'border-eve-accent/40 bg-eve-accent/5'
            : 'border-eve-border/50 bg-eve-bg/30'
        }`}
      >
        {char.portrait ? (
          <img
            src={char.portrait}
            alt=""
            className={`w-9 h-9 rounded-full border shrink-0 ${
              char.is_active ? 'border-eve-accent/60' : 'border-eve-border'
            }`}
          />
        ) : (
          <div
            className={`w-9 h-9 rounded-full border shrink-0 ${
              char.is_active ? 'border-eve-accent/40' : 'border-eve-border'
            }`}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-medium truncate ${
                char.is_active ? 'text-eve-accent' : 'text-eve-text'
              }`}
            >
              {char.name}
            </span>
            {char.is_active && (
              <span className="text-[9px] uppercase tracking-wider text-eve-accent font-semibold shrink-0">
                active
              </span>
            )}
          </div>
          {hasData ? (
            <div className="flex gap-4 text-[11px] text-eve-muted mt-0.5 font-mono flex-wrap">
              <span>
                <span className="text-eve-muted-2">wallet</span>{' '}
                <span className="text-eve-text">
                  {formatISKShort(char.wallet)}
                </span>
              </span>
              <span>
                <span className="text-eve-muted-2">SP</span>{' '}
                <span className="text-eve-text">
                  {formatISKShort(char.total_sp)}
                </span>
              </span>
              <span>
                <span className="text-eve-muted-2">jobs</span>{' '}
                <span className="text-eve-text">{char.active_jobs_count}</span>
              </span>
              <span>
                <span className="text-eve-muted-2">PI</span>{' '}
                <span className="text-eve-text">
                  {char.pi_colonies_count}
                  {char.pi_total_pins > 0 && (
                    <span className="text-eve-muted-2">
                      {' '}
                      ({char.pi_total_pins}p)
                    </span>
                  )}
                </span>
              </span>
            </div>
          ) : (
            <div className="text-[11px] text-eve-danger mt-0.5 italic">
              data unavailable — reintenta en unos minutos
            </div>
          )}
        </div>
      </div>
    </li>
  )
}
